(function () {
    'use strict';
  
    /**
     * @typedef {Object} BuilderProduct
     * @property {string} id
     * @property {string} title
     * @property {string} handle
     * @property {string} image
     * @property {string} [image2x]
     * @property {number} price
     * @property {string} variantId
     * @property {boolean} [available]
     */
  
    /**
     * @typedef {Object} BuilderCollection
     * @property {string} title
     * @property {string} handle
     * @property {BuilderProduct[]} products
     */
  
    /**
     * @typedef {Object} BuilderCategory
     * @property {string} title
     * @property {BuilderCollection[]} collections
     */
  
    /**
     * @typedef {Object} BuilderKeyring
     * @property {number} holes
     * @property {string} id
     * @property {string} variantId
     * @property {string} title
     * @property {number} price
     * @property {string} image
     * @property {boolean} [available]
     */
  
    /**
     * @typedef {Object} BuilderData
     * @property {string} currency
     * @property {string} locale
     * @property {string} browseUrl
     * @property {string} finishedUrl
     * @property {BuilderKeyring[]} keyrings
     * @property {BuilderCategory[]} categories
     */
  
    /**
     * @typedef {Object} BuilderState
     * @property {1|2|3} step
     * @property {number|null} holes
     * @property {number} activeCategoryIndex
     * @property {string|null} activeCollectionHandle
     * @property {BuilderProduct[]} selected
     * @property {number} quantity
     * @property {boolean} adding
     * @property {string|null} error
     */
  
    /** @type {BuilderData} */
    var EMPTY_DATA = {
      currency: 'CHF',
      locale: 'fr-FR',
      browseUrl: '#',
      finishedUrl: '#',
      keyrings: [],
      categories: [],
    };
  
    /**
     * Échappe une valeur pour une insertion sûre dans du HTML.
     * @param {unknown} value
     * @returns {string}
     */
    function escapeHtml(value) {
      var str = value === null || value === undefined ? '' : String(value);
      /** @type {Record<string, string>} */
      var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return str.replace(/[&<>"']/g, function (c) {
        return map[c] || c;
      });
    }
  
    /**
     * @param {number} min
     * @param {string|undefined} raw
     * @returns {number}
     */
    function toInt(raw, min) {
      var n = parseInt(raw || '', 10);
      if (isNaN(n)) return min;
      return n;
    }
  
    class BuilderConfigurator extends HTMLElement {
      constructor() {
        super();
        /** @type {BuilderState} */
        this.state = {
          step: 1,
          holes: 3,
          activeCategoryIndex: 0,
          activeCollectionHandle: null,
          selected: [],
          quantity: 1,
          adding: false,
          error: null,
        };
        /** @type {BuilderData} */
        this.data = EMPTY_DATA;
        /** @type {HTMLElement|null} */
        this.contentEl = null;
        /** @type {HTMLElement|null} */
        this.indicatorEl = null;
  
        this.handleClick = this.handleClick.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
      }
  
      connectedCallback() {
        this.indicatorEl = this.querySelector('[data-step-indicator]');
        this.contentEl = this.querySelector('[data-step-content]');
        if (!this.contentEl) return;
  
        var dataEl = this.querySelector('[data-builder-data]');
        this.data = this.parseData(dataEl);
  
        this.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleScroll, { passive: true });
    
        this.render();
      }
  
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
      }
  
      /**
       * Parse le JSON sérialisé côté Liquid. Ne lève jamais : retombe sur une
       * structure vide (le rendu reste fonctionnel, avec des messages inline).
       * @param {Element|null} dataEl
       * @returns {BuilderData}
       */
      parseData(dataEl) {
        if (!dataEl) return EMPTY_DATA;
        var raw = dataEl.textContent || '';
        if (!raw.trim()) return EMPTY_DATA;
        try {
          var parsed = JSON.parse(raw);
          return this.normalizeData(Object.assign({}, EMPTY_DATA, parsed));
        } catch (e) {
          return EMPTY_DATA;
        }
      }
  
      /**
       * Force toutes les IDs (produits, variantes) en string. Liquid sort des
       * IDs numériques via `| json`, alors que les attributs data-* du DOM
       * sont toujours des chaînes. Sans cette normalisation, les comparaisons
       * strictes (===) échouent silencieusement et rien ne peut être
       * sélectionné.
       * @param {BuilderData} data
       * @returns {BuilderData}
       */
      normalizeData(data) {
        var keyrings = (data.keyrings || []).map(function (k) {
          return Object.assign({}, k, {
            id: String(k.id),
            variantId: String(k.variantId),
          });
        });
        var categories = (data.categories || []).map(function (cat) {
          return Object.assign({}, cat, {
            collections: (cat.collections || []).map(function (coll) {
              return Object.assign({}, coll, {
                products: (coll.products || []).map(function (p) {
                  return Object.assign({}, p, {
                    id: String(p.id),
                    variantId: String(p.variantId),
                  });
                }),
              });
            }),
          });
        });
        return Object.assign({}, data, { keyrings: keyrings, categories: categories });
      }
  
      /**
       * @param {Partial<BuilderState>} patch
       */
      setState(patch) {
        this.state = Object.assign({}, this.state, patch);
        this.render();
      }
  
      /**
       * @param {string} id
       * @returns {BuilderProduct|null}
       */
      findProductById(id) {
        var categories = this.data.categories || [];
        for (var i = 0; i < categories.length; i++) {
          var collections = categories[i].collections || [];
          for (var j = 0; j < collections.length; j++) {
            var products = collections[j].products || [];
            for (var k = 0; k < products.length; k++) {
              if (products[k].id === id) return products[k];
            }
          }
        }
        return null;
      }
  
      /**
       * @param {number} cents
       * @returns {string}
       */
      formatMoney(cents) {
        var amount = (cents || 0) / 100;
        var locale = this.data.locale || 'fr-FR';
        var currency = this.data.currency || 'CHF';
        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
        } catch (e) {
          return amount.toFixed(2) + ' ' + currency;
        }
      }
  
      /**
       * @param {BuilderProduct[]} products
       * @returns {number}
       */
      sumPrices(products) {
        return products.reduce(function (sum, p) {
          return sum + (p.price || 0);
        }, 0);
      }
     
      render() {
        this.setAttribute('data-current-step', this.state.step);

        this.renderIndicator();
        if (!this.contentEl) return;
        if (this.state.step === 1) {
          this.renderStep1();
          requestAnimationFrame(this.handleScroll);
      } else if (this.state.step === 2) this.renderStep2();
        else this.renderStep3();
      }
  
      renderIndicator() {
        if (!this.indicatorEl) return;
        var items = this.indicatorEl.querySelectorAll('[data-step]');
        var s = this.state;
        var isComplete = !!(s.holes && s.selected.length === s.holes);

        items.forEach(function (el) {
          var n = toInt(el.getAttribute('data-step') || '', 0);
          el.classList.toggle('is-active', n === s.step);
          
          if (n > 1) {
            if (isComplete) {
              el.classList.remove('is-disabled');
            } else {
              el.classList.add('is-disabled');
            }
          }
        });
      }

      handleScroll() {
        if (this.state.step !== 1) return;
    
        var main = this.querySelector('.builder-main') || this.querySelector('.builder-main__library');
        var summary = this.querySelector('.builder-summary');
        
        if (!main || !summary) return;
    
        var mainRect = main.getBoundingClientRect();
        var windowHeight = window.innerHeight;
    
        // Here is the place where you want to display the little cart change just threshold 
        var scrolledPixels = windowHeight - mainRect.top;
        var threshold = mainRect.height * 0.08; //
        var isReached = scrolledPixels >= threshold;
    
        var summaryHeight = summary.offsetHeight || 150;
        var isNotEnded = mainRect.bottom > (windowHeight - summaryHeight);
    
        if (isReached && isNotEnded) {
          summary.classList.add('builder-summary--sticky');
        } else {
          summary.classList.remove('builder-summary--sticky');
        }
      }
  
      // ---------------------------------------------------------------------
      // STEP 1 — Pick
      // ---------------------------------------------------------------------
      renderStep1() {
        var s = this.state;
        var requiredCount = s.holes || 3;
        var categories = this.data.categories || [];
        var activeCategory = categories[s.activeCategoryIndex] || categories[0] || { title: '', collections: [] };
        var collections = activeCategory.collections || [];
        var activeCollection = s.activeCollectionHandle
          ? collections.filter(function (c) {
              return c.handle === s.activeCollectionHandle;
            })[0]
          : null;
        var products = activeCollection
          ? activeCollection.products
          : collections.reduce(function (acc, c) {
              return acc.concat(c.products);
            }, /** @type {BuilderProduct[]} */ ([]));
  
        var noKeyrings = !this.data.keyrings || this.data.keyrings.length === 0;
  
        var keyringsHtml = (this.data.keyrings || [])
          .map(function (k) {
            var selected = s.holes === k.holes;
            return (
              '<button type="button" class="keyring-card' +
              (selected ? ' is-selected' : '') +
              '" data-action="select-keyring" data-holes="' +
              k.holes +
              '">' +
              (selected ? '<span class="keyring-card__check" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3.5 8.5 6.5 11.5 12.5 5"></polyline></svg></span>' : '') +
              (k.image
                ? '<img class="keyring-card__img" src="' + k.image + '" alt="" width="60" height="90" loading="lazy">'
                : '') +
              '<span class="keyring-card__label">' +
              escapeHtml(k.title) +
              '</span>' +
              '</button>'
            );
          })
          .join('');
  
        var tabsHtml = categories
          .map(function (cat, i) {
            var count = (cat.collections || []).reduce(function (n, c) {
              return n + c.products.length;
            }, 0);
            return (
              '<button type="button" class="builder-tab' +
              (i === s.activeCategoryIndex ? ' is-active' : '') +
              '" data-action="select-tab" data-index="' +
              i +
              '">' +
              escapeHtml(cat.title) +
              ' <span class="builder-tab__count' +
              (i === s.activeCategoryIndex ? ' is-active' : '') +
              '">' +
              count +
              '</span></button>'
            );
          })
          .join('');
  
        var chipsHtml =
          '<button type="button" class="coll-chip' +
          (!s.activeCollectionHandle ? ' is-active' : '') +
          '" data-action="select-collection" data-handle="">entier</button>' +
          collections
            .map(function (c) {
              return (
                '<button type="button" class="coll-chip' +
                (s.activeCollectionHandle === c.handle ? ' is-active' : '') +
                '" data-action="select-collection" data-handle="' +
                escapeHtml(c.handle) +
                '">' +
                escapeHtml(c.title) +
                '</button>'
              );
            })
            .join('');
  
        var keyringChosen = !!s.holes;
        
        var tilesHtml = products.map(function (p) {
          var atLimit = s.selected.length >= requiredCount;
          var disabled = !keyringChosen || atLimit;
          
          var frontImg = p.image || (p.images && p.images.length > 0 ? p.images[0] : '');
          var backImg = p.hoverImage || (p.images && p.images.length > 1 ? p.images[1] : '');
        
          var mediaHtml = '';
          if (frontImg) {
            mediaHtml = '<span class="design-tile__img-flip">' +
                          '<img class="design-tile__img-front" src="' + frontImg + '" alt="" loading="lazy">' +
                          (backImg ? '<img class="design-tile__img-back" src="' + backImg + '" alt="" loading="lazy" onerror="this.parentElement.classList.add(\'design-tile__img-flip--no-back\')">' : '') +
                        '</span>';
          } else {
            mediaHtml = '<span class="design-tile__img--empty" aria-hidden="true"></span>';
          }
        
          return '<button type="button" class="design-tile" data-action="select-touch" data-id="' + escapeHtml(p.id) + '"' + (disabled ? ' disabled' : '') + '>' +
            mediaHtml +
          '</button>';
        }).join('');

          
  
        var libraryEmptyMsg =
          products.length === 0
            ? '<p class="builder-error">Aucun keycap disponible pour le moment' +
              (collections.length === 0 ? ' (aucune collection contenant des produits n\u2019a été trouvée)' : '') +
              '.</p>'
            : '';
  
        var html =
          '<div class="builder-step builder-step--1">' +
          '<div class="builder-row">' +
          '<div class="builder-row__label"><span class="builder-row__num">01</span> Porte-clés</div>' +
          '</div>' +
          (noKeyrings
            ? '<p class="builder-error">Aucun porte-clés n\u2019est configuré pour le moment (à choisir dans les réglages de la section).</p>'
            : '<div class="keyring-grid">' + keyringsHtml + '</div>') +
          '<div class="builder-row">' +
          '<div class="builder-row__label"><span class="builder-row__num">02</span> Keycaps </div>' +
          '<div class="builder-row__hint">' +
          s.selected.length +
          '/' +
          requiredCount +
          '</div>' +
          '</div>' +
          '<div class="builder-main">' +
          '<div class="builder-main__library">' +
          '<div class="builder-tabs">' +
          tabsHtml +
          '</div>' +
          '<div class="builder-chips">' +
          chipsHtml +
          '</div>' +
          '<div class="builder-count">' +
          products.length +
          ' modèles</div>' +
          libraryEmptyMsg +
          '<div class="design-grid">' +
          tilesHtml +
          '</div>' +
          (!keyringChosen && !noKeyrings
            ? '<p class="builder-error">Sélectionnez d\u2019abord un porte-clés ci-dessus.</p>'
            : '') +
          '</div>' +
          '<aside class="builder-summary">' +
          this.renderSummaryPanel() +
          '</aside>' +
          '</div>' +
          '</div>';
  
        if (this.contentEl) this.contentEl.innerHTML = html;
      }
  
      /**
       * @returns {string}
       */
      renderSummaryPanel() {
        var s = this.state;
        var requiredCount = s.holes || 3;
        var rows = [];
        for (var i = 0; i < requiredCount; i++) {
          var item = s.selected[i];
          rows.push(
            '<div class="summary-slot">' +
              '<div class="summary-slot__media">' +
              (item ? '<img src="' + item.image + '" alt="" width="40" height="40" loading="lazy">' : '') +
              (item
                ? '<button type="button" class="summary-slot__remove summary-slot__remove--mobile" data-action="remove-touch" data-id="' +
                  escapeHtml(item.id) +
                  '" aria-label="Retirer">\u00d7</button>'
                : '') +
              '</div>' +
              '<div class="summary-slot__info">' +
              '<span class="summary-slot__label">EMPLACEMENT ' +
              (i + 1) +
              '</span>' +
              '<span class="summary-slot__name">' +
              (item ? escapeHtml(item.title) : '') +
              '</span>' +
              '</div>' +
              (item
                ? '<button type="button" class="summary-slot__remove summary-slot__remove--desktop" data-action="remove-touch" data-id="' +
                  escapeHtml(item.id) +
                  '" aria-label="Retirer">\u00d7</button>'
                : '') +
              '</div>'
          );
        }
        var complete = !!s.holes && s.selected.length === requiredCount;
        return (
          '<div class="summary-panel">' +
          '<div class="summary-panel__header">' +
          '<span class="summary-panel__title">MON ENSEMBLE \u00b7 ' +
          requiredCount +
          ' PIÈCES</span>' +
          '<span class="summary-panel__count">' +
          s.selected.length +
          '/' +
          requiredCount +
          '</span>' +
          '</div>' +
          '<div class="summary-panel__list">' +
          rows.join('') +
          '</div>' +
          '<button type="button" class="btn btn--primary btn--block" data-action="go-next"' +
          (complete ? '' : ' disabled') +
          '>Suivant \u2192</button>' +
          '</div>'
        );
      }
  
      // ---------------------------------------------------------------------
      // STEP 2 — Confirm
      // ---------------------------------------------------------------------
      renderStep2() {
        var s = this.state;
        var keyring = (this.data.keyrings || []).filter(function (k) {
          return k.holes === s.holes;
        })[0];
        var touchesPrice = this.sumPrices(s.selected);
        var keyringPrice = keyring ? keyring.price : 0;
        var unitTotal = touchesPrice + keyringPrice;
        var grandTotal = unitTotal * s.quantity;
  
        var thumbsHtml = s.selected
          .map(function (t) {
            return t.image
              ? '<img class="confirm-thumb" src="' + t.image + '" alt="" width="96" height="96" loading="lazy">'
              : '';
          })
          .join('');
  
        var html =
          '<div class="builder-step builder-step--2">' +
          '<h2 class="builder-step__title">Contrôle ton choix et confirme.</h2>' +
          '<div class="confirm-card">' +
          '<div class="confirm-card__thumbs">' +
          thumbsHtml +
          '</div>' +
          '<div class="confirm-card__detail">' +
          '<span class="confirm-card__eyebrow">MA COMBINAISON :</span>' +
          '<h3 class="confirm-card__title">Porte-clés à ' +
          s.selected.length +
          ' keycaps</h3>'+
          '<p class="confirm-card__price">Keycaps ' +
          this.formatMoney(touchesPrice) +
          ' + Porte-clés ' +
          this.formatMoney(keyringPrice) +
          ' = <strong>' +
          this.formatMoney(unitTotal) +
          '</strong> / pièce</p>' +
          '<div class="confirm-card__qty">' +
          '<div class="qty-stepper">' +
          '<button type="button" class="qty-stepper__btn" data-action="qty-dec" aria-label="Diminuer">\u2212</button>' +
          '<span class="qty-stepper__value">' +
          s.quantity +
          '</span>' +
          '<button type="button" class="qty-stepper__btn" data-action="qty-inc" aria-label="Augmenter">+</button>' +
          '</div>' +
          '</div>' +
          '<p class="confirm-card__total">Total <strong>' +
          this.formatMoney(grandTotal) +
          '</strong></p>' +
          '</div>' +
          '</div>' +
          '<div class="builder-actions">' +
          '<button type="button" class="btn btn--ghost" data-action="go-back">\u2190 Sélectionne à nouveau</button>' +
          '<button type="button" class="btn btn--primary" data-action="go-confirm">Confirmer \u2192</button>' +
          '</div>' +
          '</div>';
  
          
        if (this.contentEl) this.contentEl.innerHTML = html;
        if (this.contentEl) {
          this.contentEl.innerHTML = html;
          this.handleScroll();
        }
      }
  
      // ---------------------------------------------------------------------
      // STEP 3 — Checkout
      // ---------------------------------------------------------------------
      renderStep3() {
        var s = this.state;
        var keyring = (this.data.keyrings || []).filter(function (k) {
          return k.holes === s.holes;
        })[0];
        var touchesTotal = this.sumPrices(s.selected) * s.quantity;
        var keyringTotal = (keyring ? keyring.price : 0) * s.quantity;
        var grandTotal = touchesTotal + keyringTotal;
  
        var thumbsHtml = s.selected
          .map(function (t) {
            return t.image
              ? '<img class="confirm-thumb" src="' + t.image + '" alt="" width="96" height="96" loading="lazy">'
              : '';
          })
          .join('');
  
        var html =
          '<div class="builder-step builder-step--3">' +
          '<div class="checkout-card">' +
          '<div class="checkout-card__thumbs">' +
          thumbsHtml +
          '</div>' +
          '<div class="checkout-card__summary">' +
          '<span class="checkout-card__eyebrow">RÉSUMÉ DE LA COMMANDE</span>' +
          '<div class="checkout-line"><span>Keycaps' +
          (s.quantity > 1 ? 'x' : '') +
          '</span><span>' +
          this.formatMoney(touchesTotal) +
          '</span></div>' +
          '<div class="checkout-line"><span>Porte-Clés (' +
          (s.holes || 0) +
          ' pièces) \u00d7 ' +
          (s.quantity > 1 ? 's' : '') +
          '</span><span>' +
          this.formatMoney(keyringTotal) +
          '</span></div>' +
          '<div class="checkout-line checkout-line--muted"><span>Livraison</span><span>Calculer lors de l\u2019étape de paiement</span></div>' +
          '<div class="checkout-divider"></div>' +
          '<div class="checkout-line checkout-line--total"><span>Total</span><span>' +
          this.formatMoney(grandTotal) +
          '</span></div>' +
          (s.error ? '<p class="builder-error">' + 
            (s.error.toLowerCase().includes('stock') || s.error.toLowerCase().includes('sold') || s.error.toLowerCase().includes('inventory') 
              ? "Ce produit est actuellement en rupture de stock." 
              : "Une erreur est survenue : " + escapeHtml(s.error)) + 
            '</p>' : '') +
          '<button type="button" class="btn btn--primary btn--block" data-action="add-to-cart"' + (s.adding ? ' disabled' : '') + '>' + (s.adding ? "Ajout en cours..." : "Ajouter au panier") + '</button>' +
          '<button type="button" class="checkout-option" data-action="restart">' +
            '<span class="checkout-option__icon" aria-hidden="true">\u21ba</span>' +
            '<span><strong>Crée une nouvelle combinaison</strong></span>' +
          '</button>' +
          '<a class="checkout-option" href="' + escapeHtml(this.data.browseUrl || '#') + '">' +
            '<span class="checkout-option__icon" aria-hidden="true">\u2192</span>' +
            '<span><strong>Ajoute des keycaps supplémentaires</strong></span>' +
          '</a>' +
          '</div></div></div>';
  
        if (this.contentEl) this.contentEl.innerHTML = html;
        if (this.contentEl) {
          this.contentEl.innerHTML = html;
          this.handleScroll();
        }
      }
  
      // ---------------------------------------------------------------------
      // Events
      // ---------------------------------------------------------------------
      /**
       * @param {MouseEvent} event
       */
      handleClick(event) {
        var target = /** @type {HTMLElement|null} */ (event.target instanceof Element ? event.target : null);
        if (!target) return;
        var btn = /** @type {HTMLElement|null} */ (target.closest('[data-action]'));
        if (!btn || !this.contains(btn)) return;
  
        var action = btn.getAttribute('data-action') || '';
        var s = this.state;
  
        switch (action) {
          case 'select-keyring': {
            var holes = toInt(btn.getAttribute('data-holes') || '', 0);
            var selected = s.selected;
            if (selected.length > holes) selected = selected.slice(0, holes);
            this.setState({ holes: holes, selected: selected });
            break;
          }
          case 'select-tab': {
            var index = toInt(btn.getAttribute('data-index') || '', 0);
            this.setState({ activeCategoryIndex: index, activeCollectionHandle: null });
            break;
          }
          case 'select-collection': {
            var handle = btn.getAttribute('data-handle') || null;
            this.setState({ activeCollectionHandle: handle });
            break;
          }
          case 'select-touch': {
            var touchId = btn.getAttribute('data-id');
            
            var touchItem = null;
            var categories = this.data.categories || [];
            for (var i = 0; i < categories.length; i++) {
              var collections = categories[i].collections || [];
              for (var j = 0; j < collections.length; j++) {
                var products = collections[j].products || [];
                var match = products.find(function (p) {
                  return p.id === touchId;
                });
                if (match) {
                  touchItem = match;
                  break;
                }
              }
              if (touchItem) break;
            }

            if (!touchItem) break;

            if (s.selected.length >= s.holes) {
              break;
            }

            var newSelected = s.selected.concat([touchItem]);
            
            this.setState({ selected: newSelected });
            break;
          }
          case 'remove-touch': {
            var removeId = btn.getAttribute('data-id') || '';
            this.setState({
              selected: s.selected.filter(function (t) {
                return t.id !== removeId;
              }),
            });
            break;
          }
          case 'go-next': {
            if (s.holes && s.selected.length === s.holes) {
              this.setState({ step: 2 });
              this.scrollIntoView({ behavior: 'smooth', block: 'start' });
              
            }
            break;
          }
          case 'goto-step': {
            var targetStep = toInt(btn.getAttribute('data-target-step') || '', 1);
            
            if (targetStep > 1) {
              if (!s.holes || s.selected.length !== s.holes) {
                return; 
              }
            }

            this.setState({ step: targetStep });
            
            if (targetStep === 1) {
              this.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            break;
          }
          case 'go-back': {
            this.setState({ step: 1 });
            break;
          }
          case 'go-confirm': {
            this.setState({ step: 3, error: null });
            break;
          }
          case 'qty-inc': {
            this.setState({ quantity: s.quantity + 1 });
            break;
          }
          case 'qty-dec': {
            this.setState({ quantity: Math.max(1, s.quantity - 1) });
            break;
          }
          case 'restart': {
            this.setState({
              step: 1,
              holes: null,
              selected: [],
              quantity: 1,
              activeCategoryIndex: 0,
              activeCollectionHandle: null,
              error: null,
            });
            break;
          }
          case 'add-to-cart': {
            this.addToCart();
            break;
          }
          default:
            break;
        }
      }
  
      async addToCart() {
        var s = this.state;
        var keyring = (this.data.keyrings || []).filter(function (k) {
          return k.holes === s.holes;
        })[0];
  
        if (!keyring || !keyring.variantId || s.selected.length === 0) {
          this.setState({ error: 'Configuration incomplète : porte-clés ou keycaps manquants.' });
          return;
        }
  
        this.setState({ adding: true, error: null });
  
        var configId = 'cfg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  
        /** @type {Array<{id: string, quantity: number, properties: Record<string, string>}>} */
        var items = [
          {
            id: String(keyring.variantId),
            quantity: Number(s.quantity) || 1,
            properties: { _config_id: configId },
          },
        ];
  
        s.selected.forEach(function (t, i) {
          var variantId = t.variantId || t.id;
          items.push({
            id: String(variantId),
            quantity: Number(s.quantity) || 1,
            properties: { 
              _config_id: configId, 
              'Emplacement': String(i + 1) 
            },
          });
        });
  
        var win = /** @type {any} */ (window);
        var root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
  
        try {
          var response = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          });
  
          if (!response.ok) {
            var err = await response.json().catch(function () {
              return {};
            });
            throw new Error(err.description || err.message || 'Erreur lors de l’ajout au panier.');
          }
  
          var result = await response.json();
  
          this.dispatchEvent(new CustomEvent('builder:added', { bubbles: true, detail: { items: result } }));
  
          window.location.href = root + 'cart';
        } catch (error) {
          var message = error instanceof Error ? error.message : 'Une erreur est survenue.';
          this.setState({ adding: false, error: message });
        }
      }
    }
  
    if (!customElements.get('builder-configurator')) {
      customElements.define('builder-configurator', BuilderConfigurator);
    }
  })();